import { defineStore } from "pinia";
import { createDirectus, staticToken, realtime } from "@directus/sdk";
import { backAdresse } from "boot/utils";
import { LocalStorage } from "quasar";
import { useAuthUnifiedStore } from "./authUnified.js";

export const useRealtimeStore = defineStore("realtime", {
  state: () => ({
    realtimeClient: null,
    subscriptions: new Map(),
    isConnected: false,
    handlers: new Map(),
    isInitialized: false, // Flag pour éviter les réinitialisations
    initializationPromise: null, // Promesse pour gérer les appels parallèles
  }),

  actions: {
    // Helper: extrait un appelant lisible depuis Error.stack
    _getCaller() {
      try {
        const stack = new Error().stack?.split("\n") || [];
        // Chercher la première ligne utile hors node_modules/pinia
        for (let i = 2; i < stack.length; i++) {
          const line = stack[i].trim();
          if (
            line &&
            !line.includes("node_modules") &&
            !line.includes("pinia.js") &&
            !line.includes("@vite") &&
            !line.includes("vite")
          ) {
            return line;
          }
        }
        // fallback
        return stack[2]?.trim() || "unknown";
      } catch {
        return "unknown";
      }
    },
    async initialize(caller) {
      const authStore = useAuthUnifiedStore();

      if (!authStore.isAuthenticated) {
        return;
      }

      // Vérifier d'abord si realtimeClient existe déjà et est connecté
      if (this.realtimeClient && this.isConnected) {
        // Déjà initialisé et connecté, ne rien faire
        return;
      }

      // Si une initialisation est déjà en cours, attendre qu'elle se termine
      if (this.initializationPromise) {
        return this.initializationPromise;
      }

      // Si realtimeClient existe mais pas connecté, reconnecter
      if (this.realtimeClient && !this.isConnected) {
        try {
          this.connect();
        } catch (error) {
          // Si erreur "already open", c'est OK
          const isAlreadyOpen =
            error.message?.includes('state is "open"') ||
            error.message?.includes("Cannot connect");
          if (!isAlreadyOpen) {
            throw error;
          }
        }
        return;
      }

      // Créer une promesse pour gérer les appels parallèles
      this.initializationPromise = (async () => {
        try {
          this.realtimeClient = createDirectus(backAdresse)
            .with(staticToken(authStore.sessionToken))
            .with(realtime());

          this.setupWebSocketHandlers();
          this.isInitialized = true;
          this.connect();
        } catch (error) {
          // Gérer l'erreur "already open" si elle se produit
          const isAlreadyOpen =
            error.message?.includes('state is "open"') ||
            error.message?.includes("Cannot connect");
          if (isAlreadyOpen) {
            this.isConnected = true;
          } else {
            throw error;
          }
        } finally {
          // Libérer la promesse après initialisation
          this.initializationPromise = null;
        }
      })();

      return this.initializationPromise;
    },

    setupWebSocketHandlers() {
      if (!this.realtimeClient) return;

      this.realtimeClient.onWebSocket("open", () => {
        this.isConnected = true;
      });

      this.realtimeClient.onWebSocket("close", () => {
        this.isConnected = false;
      });

      this.realtimeClient.onWebSocket("error", (error) => {
        this.isConnected = false;
      });

      this.realtimeClient.onWebSocket("message", (message) => {
        // Ignorer totalement les messages de type "subscription" (gérés ailleurs)
        if (message.type === "subscription") return;
        const handler = this.handlers.get(message.type);
        if (handler) {
          handler(message.data);
        }
      });
    },

    async subscribe(collection, event, handler, filter = {}, fields = ["*"]) {
      if (!this.realtimeClient) {
        return;
      }

      const subscriptionKey = `${collection}-${event}`;

      // Vérifier si la souscription existe déjà
      if (this.subscriptions.has(subscriptionKey)) {
        return;
      }

      try {
        const { subscription } = await this.realtimeClient.subscribe(
          collection,
          {
            event,
            query: {
              filter: filter,
              fields: fields,
            },
          }
        );

        // Stocker la souscription
        this.subscriptions.set(subscriptionKey, subscription);

        // Stocker le handler pour ce type d'événement
        this.handlers.set(event, handler);

        // Démarrer l'écoute des messages
        this.startSubscriptionListener(subscription, event);
      } catch (error) {
        // Gestion silencieuse des erreurs en production
      }
    },

    async startSubscriptionListener(subscription, event) {
      try {
        for await (const item of subscription) {
          if (item.event === event) {
            const handler = this.handlers.get(event);

            if (handler) {
              handler(item);
            }
          }
        }
      } catch (error) {
        // Gestion silencieuse des erreurs en production
      }
    },

    unsubscribe(collection, event) {
      const subscriptionKey = `${collection}-${event}`;
      const subscription = this.subscriptions.get(subscriptionKey);

      if (subscription) {
        try {
          this.realtimeClient.dispose();
          this.subscriptions.delete(subscriptionKey);
          this.handlers.delete(event);
        } catch (error) {
          // Gestion silencieuse des erreurs en production
        }
      }
    },

    connect() {
      // Éviter les connexions multiples
      if (this.isConnected) {
        return;
      }

      if (!this.realtimeClient) {
        return;
      }

      try {
        this.realtimeClient.connect("json");
      } catch (error) {
        // Gérer l'erreur "Cannot connect when state is 'open'"
        // Cela peut arriver si le WebSocket est déjà ouvert mais isConnected n'est pas encore mis à jour
        const isAlreadyOpen =
          error.message?.includes('state is "open"') ||
          error.message?.includes("Cannot connect");

        if (isAlreadyOpen) {
          // Le WebSocket est déjà ouvert, mettre à jour le flag
          this.isConnected = true;
          return;
        }

        // Autre erreur, la propager
        throw error;
      }
    },

    disconnect() {
      if (this.realtimeClient) {
        // Désinscrire toutes les souscriptions
        for (const [key, subscription] of this.subscriptions) {
          try {
            subscription.dispose();
          } catch (error) {
            // Gestion silencieuse des erreurs en production
          }
        }
        this.subscriptions.clear();
        this.handlers.clear();
        this.realtimeClient.disconnect();
        this.isConnected = false;
        this.isInitialized = false; // Réinitialiser le flag pour permettre une nouvelle initialisation
        this.initializationPromise = null; // Réinitialiser la promesse
      }
    },

    getSubscriptionStatus(collection, event) {
      const subscriptionKey = `${collection}-${event}`;
      return {
        exists: this.subscriptions.has(subscriptionKey),
        key: subscriptionKey,
        subscription: this.subscriptions.get(subscriptionKey),
        isConnected: this.isConnected,
        hasHandler: this.handlers.has(event),
      };
    },

    // Fonction pour envoyer un message via WebSocket
    sendMessage(collection, data) {
      if (!this.realtimeClient || !this.isConnected) {
        throw new Error("Realtime client non connecté");
      }

      return this.realtimeClient.sendMessage({
        type: "items",
        collection: collection,
        action: "create",
        data: data,
      });
    },

    fetchMessage(collection, data) {
      if (!this.realtimeClient || !this.isConnected) {
        throw new Error("Realtime client non connecté");
      }
      try {
        const params = {
          type: "items",
          collection: collection,
          action: "read",
          query: data,
        };
        return this.realtimeClient.sendMessage(params);
      } catch (error) {
        throw error;
      }
    },

    // Fonction spécifique pour envoyer un message de chat
    async sendChatMessage(messageData) {
      if (!this.isConnected) {
        throw new Error("Realtime non connecté");
      }

      try {
        const result = await this.sendMessage("chatMessage", {
          reservation: messageData.reservation,
          message: messageData.message,
          status: messageData.status || "not_read",
        });
        return result;
      } catch (error) {
        throw error;
      }
    },
  },
});
